// Admin Loyalty — search members, view balances/history, apply adjustments.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Search, Plus, Minus } from "lucide-react";
import { adminApi } from "@/lib/adminAuth";

const usd = (n) => Number(n || 0).toLocaleString();

export default function AdminLoyalty() {
    const [q, setQ] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [adjust, setAdjust] = useState({ delta: 0, reason: "" });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await adminApi.get(`/admin/loyalty/members${q ? `?q=${encodeURIComponent(q)}` : ""}`);
            setRows(data);
        } catch { toast.error("Could not load members"); }
        finally { setLoading(false); }
    };
    useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

    const open = async (customerId) => {
        setSelected(customerId);
        setDetail(null);
        try {
            const { data } = await adminApi.get(`/admin/loyalty/${customerId}`);
            setDetail(data);
        } catch { toast.error("Could not load loyalty detail"); }
    };

    const submitAdjust = async () => {
        if (!adjust.delta || !adjust.reason.trim()) return toast.error("Enter both amount and reason");
        setBusy(true);
        try {
            await adminApi.post(`/admin/loyalty/${selected}/adjust`, adjust);
            toast.success("Adjustment saved");
            setAdjust({ delta: 0, reason: "" });
            await open(selected);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not adjust");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="admin-loyalty-page">
            <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Loyalty</div>
                    <h1 className="font-display text-4xl md:text-5xl text-[#0b1f22] mt-3">Cove Rewards members</h1>
                    <p className="text-[#0b1f22]/60 mt-2 text-sm">Search customer accounts, view point balances, and apply audited adjustments.</p>
                </div>
                <button
                    data-testid="run-bonuses"
                    onClick={async () => {
                        try {
                            const { data } = await adminApi.post("/admin/loyalty/run-bonuses");
                            toast.success(`Bonus scan · ${data.birthday} birthday · ${data.anniversary} anniversary`);
                            load();
                        } catch { toast.error("Could not run bonus scan"); }
                    }}
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] px-4 py-2 rounded-full border border-gold/60 text-gold hover:bg-gold hover:text-navy transition-colors"
                >
                    <Sparkles size={13} /> Run birthday & anniversary scan
                </button>
            </header>

            <div className="mb-4 relative max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by name, email or phone"
                    data-testid="loyalty-search"
                    className="w-full pl-9 pr-3 py-2.5 rounded-full border border-navy/15 text-sm focus:border-gold focus:outline-none bg-white"
                />
            </div>

            <div className="grid lg:grid-cols-[1fr_1.2fr] gap-6">
                {/* Members list */}
                <div className="bg-white rounded-2xl border border-navy/10 overflow-hidden max-h-[70vh] overflow-y-auto">
                    {loading ? <div className="p-8 text-sm text-navy/60">Loading members…</div> : rows.length === 0 ? (
                        <div className="p-10 text-center text-navy/50">
                            <Sparkles size={20} className="mx-auto mb-2 text-navy/30" /> No members yet
                        </div>
                    ) : rows.map((c) => (
                        <button
                            key={c.user_id}
                            onClick={() => open(c.user_id)}
                            data-testid={`loyalty-row-${c.user_id}`}
                            className={`w-full text-left px-5 py-4 border-b border-navy/10 hover:bg-navy/5 transition-colors ${selected === c.user_id ? "bg-navy/5" : ""}`}
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <div className="font-medium text-navy">{c.name || "—"}</div>
                                    <div className="text-[11px] text-navy/50 mt-0.5">{c.email}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-display text-lg text-navy">{usd(c.loyalty?.available)}</div>
                                    <div className="text-[10px] uppercase tracking-[0.14em] text-gold mt-0.5">{c.loyalty?.tier}</div>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>

                {/* Detail panel */}
                <div>
                    {!selected ? (
                        <div className="bg-white rounded-2xl border border-navy/10 p-10 text-center text-navy/50 text-sm">
                            Select a member to view balance, history, and apply adjustments.
                        </div>
                    ) : !detail ? (
                        <div className="bg-white rounded-2xl border border-navy/10 p-10 text-sm text-navy/60">Loading…</div>
                    ) : (
                        <div className="space-y-4" data-testid="loyalty-detail">
                            <section className="bg-white rounded-2xl border border-navy/10 p-6">
                                <div className="flex items-start justify-between flex-wrap gap-3">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{detail.balance.tier}</div>
                                        <div className="font-display text-2xl text-navy mt-1">{detail.customer.name}</div>
                                        <div className="text-[11px] text-navy/50 mt-1">{detail.customer.email}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-display text-3xl text-navy">{usd(detail.balance.available)}</div>
                                        <div className="text-[10px] uppercase tracking-[0.14em] text-navy/50">available</div>
                                    </div>
                                </div>
                                <div className="mt-4 grid grid-cols-4 gap-2 text-center">
                                    <Mini l="Pending" v={detail.balance.pending} />
                                    <Mini l="Redeemed" v={detail.balance.redeemed} />
                                    <Mini l="Expired" v={detail.balance.expired} />
                                    <Mini l="Lifetime" v={detail.balance.lifetime_earned} />
                                </div>
                            </section>

                            <section className="bg-white rounded-2xl border border-navy/10 p-6">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-navy/50 mb-3">Manual adjustment</div>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => setAdjust({ ...adjust, delta: Math.abs(adjust.delta) })} className={`p-2 rounded-full ${adjust.delta >= 0 ? "bg-jade text-white" : "bg-navy/5 text-navy"}`}><Plus size={13} /></button>
                                    <button onClick={() => setAdjust({ ...adjust, delta: -Math.abs(adjust.delta) })} className={`p-2 rounded-full ${adjust.delta < 0 ? "bg-red-500 text-white" : "bg-navy/5 text-navy"}`}><Minus size={13} /></button>
                                    <input type="number" placeholder="Points" value={Math.abs(adjust.delta) || ""} onChange={(e) => setAdjust({ ...adjust, delta: (adjust.delta < 0 ? -1 : 1) * (Number(e.target.value) || 0) })}
                                        data-testid="loyalty-adjust-delta" className="w-24 border border-navy/15 rounded-lg px-3 py-2 text-sm" />
                                    <input type="text" placeholder="Reason (required — logged)" value={adjust.reason} onChange={(e) => setAdjust({ ...adjust, reason: e.target.value })}
                                        data-testid="loyalty-adjust-reason" className="flex-1 border border-navy/15 rounded-lg px-3 py-2 text-sm" />
                                    <button onClick={submitAdjust} disabled={busy} data-testid="loyalty-adjust-save" className="bg-navy text-ivory px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.16em] disabled:opacity-60">
                                        {busy ? "…" : "Apply"}
                                    </button>
                                </div>
                            </section>

                            <section className="bg-white rounded-2xl border border-navy/10 overflow-hidden">
                                <div className="px-5 py-3 border-b border-navy/10 text-[10px] uppercase tracking-[0.22em] text-navy/50">History</div>
                                {detail.history.length === 0 ? (
                                    <div className="px-5 py-6 text-sm text-navy/50 text-center">No activity</div>
                                ) : (
                                    <div className="max-h-80 overflow-y-auto">
                                        {detail.history.map((h) => (
                                            <div key={h.id} className="px-5 py-3 border-b border-navy/10 flex items-center justify-between">
                                                <div>
                                                    <div className="text-sm text-navy">{h.reason || h.kind}</div>
                                                    <div className="text-[10px] uppercase tracking-[0.14em] text-navy/40 mt-0.5">
                                                        {h.kind} · {h.status} · {new Date(h.created_at).toLocaleDateString()}
                                                    </div>
                                                </div>
                                                <div className={`font-display ${h.points > 0 ? "text-jade" : "text-red-600"}`}>{h.points > 0 ? "+" : ""}{h.points}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Mini({ l, v }) {
    return (
        <div className="bg-navy/[0.03] rounded-lg py-2">
            <div className="text-[9px] uppercase tracking-[0.14em] text-navy/50">{l}</div>
            <div className="font-display text-lg text-navy">{usd(v)}</div>
        </div>
    );
}
